<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import type { NodeFit, SensitivityPoint } from '../model/fit'
import type { Interval } from '../model/ranking'
import CostPanel from './CostPanel.vue'
import NodeDiagram from './NodeDiagram.vue'
import StackedBar from './StackedBar.vue'
import type { SlotId } from './types'

const props = defineProps<{
  fit: NodeFit
  slotId: SlotId
  /** The slot the instance list currently fills. */
  active: boolean
  /** Whether a second slot is on screen, so the slot letters mean something. */
  comparing: boolean
  points: readonly SensitivityPoint[]
  step: number
  interval: Interval
  hours: number
}>()

const emit = defineEmits<{ activate: []; remove: []; 'update:step': [number] }>()

const { t } = useI18n()
const format = useFormat()

const RESOURCES = ['cpu', 'memory'] as const

const instance = computed(() => props.fit.instance)

const meter = (ratio: number) => [
  {
    key: 'used',
    value: Math.min(1, ratio),
    color: '--color-chart-1',
    title: format.percent(ratio),
  },
  { key: 'rest', value: Math.max(0, 1 - ratio), color: 'none', title: '' },
]

const overcommit = computed(() =>
  RESOURCES.map((resource) => {
    const value = props.fit.overcommit[resource]
    return { resource, value, over: value !== null && value > 1 }
  }),
)
</script>

<template>
  <article
    class="summary"
    :class="{ 'summary--active': active && comparing }"
    :aria-labelledby="`summary-${slotId}-name`"
  >
    <header class="summary__header">
      <span v-if="comparing" class="summary__slot" aria-hidden="true">{{
        slotId.toUpperCase()
      }}</span>
      <div class="summary__title">
        <h3 :id="`summary-${slotId}-name`" class="summary__name mono">
          <span v-if="comparing" class="visually-hidden">{{
            t('slot.label', { slot: slotId.toUpperCase() })
          }}</span>
          {{ instance.name }}
        </h3>
        <p class="summary__specs">
          {{ t(`provider.service.${instance.provider}`) }} · {{ format.cpu(fit.cpu.capacity) }} ·
          {{ format.memory(fit.memory.capacity) }} ·
          {{ t('units.perHour', { price: format.price(instance.hourlyPrice) }) }}
        </p>
      </div>
      <div class="summary__actions">
        <button v-if="comparing && !active" type="button" @click="emit('activate')">
          {{ t('slot.change') }}
        </button>
        <span v-else-if="comparing" class="summary__picking">{{ t('slot.picking') }}</span>
        <button v-if="slotId === 'b'" type="button" @click="emit('remove')">
          {{ t('slot.remove') }}
        </button>
      </div>
    </header>

    <div class="summary__headline">
      <p class="summary__big">
        <span class="summary__number mono">{{ format.price(fit.costPerPod * hours) }}</span>
        <span class="summary__unit">{{ t(`summary.perPodPer.${interval}`) }}</span>
      </p>
      <p class="summary__fit">
        <template v-if="fit.pods > 0">
          {{ t('summary.podsPerNode', { count: format.count(fit.pods) }, fit.pods) }} ·
          {{ t(`summary.limitedBy.${fit.limitedBy}`) }}
        </template>
        <template v-else>{{ t('summary.noFit') }}</template>
      </p>
      <p v-if="instance.burstable" class="summary__note">{{ t('summary.burstable') }}</p>
      <p v-if="instance.arm" class="summary__note">{{ t('summary.arm') }}</p>
    </div>

    <dl class="summary__tiles">
      <div class="summary__tile">
        <dt>
          {{ t('summary.maxPods') }}
          <InfoTip
            :label="t('summary.maxPods')"
            :text="t(`summary.maxPodsHelp.${instance.provider}`)"
          />
        </dt>
        <dd class="mono">
          {{
            t('summary.podsOfLimit', {
              pods: format.count(fit.pods),
              limit: format.count(fit.maxPods),
            })
          }}
        </dd>
      </div>
      <div class="summary__tile">
        <dt>
          {{ t('summary.volumes') }}
          <InfoTip :label="t('summary.volumes')" :text="t('summary.volumesHelp')" />
        </dt>
        <dd class="mono">
          {{ instance.maxVolumes === null ? t('units.none') : format.count(instance.maxVolumes) }}
        </dd>
      </div>
      <div class="summary__tile summary__tile--wide">
        <dt>
          {{ t('summary.efficiency') }}
          <InfoTip :label="t('summary.efficiency')" :text="t('summary.efficiencyHelp')" />
        </dt>
        <dd>
          <span v-for="resource in RESOURCES" :key="resource" class="summary__meter">
            <span class="summary__meter-label">{{ t(`node.${resource}`) }}</span>
            <StackedBar :segments="meter(fit.efficiency[resource])" />
            <span class="mono">{{ format.percent(fit.efficiency[resource]) }}</span>
          </span>
        </dd>
      </div>
      <div class="summary__tile summary__tile--wide">
        <dt>
          {{ t('summary.overcommit') }}
          <InfoTip :label="t('summary.overcommit')" :text="t('summary.overcommitHelp')" />
        </dt>
        <dd class="summary__overcommit">
          <span
            v-for="item in overcommit"
            :key="item.resource"
            :class="{ summary__over: item.over }"
          >
            {{ t(`node.${item.resource}`) }}
            <span class="mono">{{
              item.value === null ? t('summary.noLimit') : format.percent(item.value)
            }}</span>
            <template v-if="item.over">
              <span aria-hidden="true"> ⚠</span>
              <span class="visually-hidden">{{ t('summary.overcommitted') }}</span>
            </template>
          </span>
        </dd>
      </div>
    </dl>

    <NodeDiagram :fit="fit" />

    <CostPanel
      :fit="fit"
      :points="points"
      :step="step"
      :hours="hours"
      @update:step="emit('update:step', $event)"
    />
  </article>
</template>

<style scoped>
.summary {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-width: 0;
  padding: var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
}

.summary--active {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 1px var(--color-accent);
}

.summary__header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.summary__slot {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: var(--radius-sm);
  background: var(--color-accent-subtle);
  color: var(--color-accent);
  font-weight: 700;
}

.summary__title {
  flex: 1;
  min-width: 0;
}

.summary__name {
  margin: 0;
  font-size: var(--text-lg);
  overflow-wrap: anywhere;
}

.summary__specs {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.summary__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-2);
}

.summary__picking {
  font-size: var(--text-sm);
  color: var(--color-accent);
}

.summary__headline p {
  margin: 0;
}

.summary__big {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
}

.summary__number {
  font-size: 2rem;
  font-weight: 600;
  line-height: 1.1;
  color: var(--color-accent);
}

.summary__unit,
.summary__note {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.summary__fit {
  font-weight: 500;
}

.summary__tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
  margin: 0;
}

.summary__tile {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
}

.summary__tile--wide {
  grid-column: 1 / -1;
}

.summary__tile dt {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.summary__tile dd {
  margin: 0;
  font-size: var(--text-lg);
}

.summary__meter {
  display: grid;
  grid-template-columns: 4.5rem 1fr 3rem;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
}

.summary__meter .mono {
  text-align: right;
}

.summary__meter-label {
  color: var(--color-text-muted);
}

.summary__overcommit {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  font-size: var(--text-base);
}

.summary__over {
  color: var(--color-warning);
}

button {
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

button:hover {
  background: var(--color-surface-raised);
  border-color: var(--color-border-strong);
}
</style>
